import React, { useMemo, useCallback } from 'react'
import { useDispatch } from 'react-redux'

import toLower from 'lodash/toLower'

import { useDialog } from '@galo-monitor/dialog'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { ModelActions } from '@galo-monitor/stores/model'
import DataTable from '@galo-monitor/web-components/DataTable'
import DataTableIcons from '@galo-monitor/web-components/DataTable/icons'
import ConfirmationModal from '@galo-monitor/web-containers/modals/ConfirmationModal'
import ErrorModal from '@galo-monitor/web-containers/modals/ErrorModal'
import ModelModal from '@galo-monitor/web-containers/modals/root/ModelModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'

const Models = () => {
  const t = useT()
  const snackbar = useSnackbar()
  const { createDialog } = useDialog()
  const dispatch = useCallback(useDispatch(), [])
  const { routes, getActionsBySlug } = useRoutes()

  const { data: models, mutate, isValidating } = useGaloMonitorApi('/admin/machines/models')

  const userActions = useMemo(
    () => getActionsBySlug(routes.brandsModels.slug),
    [getActionsBySlug, routes.brandsModels.slug]
  )

  const columns = useMemo(
    () => [
      {
        title: t('name'),
        field: 'name'
      },
      {
        title: t('brand', { howMany: 1 }),
        field: 'maker'
      },
      {
        title: t('type', { howMany: 1 }),
        field: 'type'
      }
    ],
    [t]
  )

  const onAddClick = useCallback(
    () => {
      createDialog({
        id: 'create-model',
        Component: ModelModal,
        props: {
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onEditClick = useCallback(
    (_, model) => {
      createDialog({
        id: 'edit-model',
        Component: ModelModal,
        props: {
          model,
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onSoftDeleteClick = useCallback(
    (_, model) => {
      createDialog({
        id: 'confirmation',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to {action} the "{name}" {this}?', {
            gender: 'male',
            howMany: 1,
            this: t('model', { howMany: 1 }),
            action: t('inactivate'),
            name: model.name
          }),
          onConfirm: () => {
            dispatch(ModelActions.removeModel(
              model,
              () => {
                snackbar.success(t('{this} {action} successfully!', {
                  this: t('model', { howMany: 1 }),
                  action: toLower(t('inactivated', { gender: 'male', howMany: 1 }))
                }))
                mutate()
              },
              () => {
                createDialog({
                  id: 'models-error',
                  Component: ErrorModal,
                  props: {
                    title: `${ t('could not {action} the {this}', {
                      howMany: 1,
                      gender: 'male',
                      action: toLower(t('inactivate')),
                      this: toLower(t('model', { howMany: 1 }))
                    })  }.`
                  }
                })
              }
            ))
          }
        }
      })
    },
    [createDialog, dispatch, mutate, snackbar, t]
  )

  const actions = useMemo(
    () => {
      const datatableActions = []
      if (userActions.delete) {
        datatableActions.push(
          () => ({
            icon: DataTableIcons.Delete,
            tooltip: t('datagrid body inactivate tooltip'),
            onClick: onSoftDeleteClick,
            disabled: isValidating,
            iconButtonProps: {
              size: 'small'
            },
            position: 'row'
          })
        )
      }

      return datatableActions
    },
    [isValidating, onSoftDeleteClick, t, userActions.delete]
  )

  return (
    <DataTable
      loading={ isValidating }
      data={ models || [] }
      actions={ actions }
      columns={ columns }
      onAddClick={ userActions.write && onAddClick }
      onEditClick={ userActions.edit && onEditClick }
      customActionsAtLast
    />
  )
}

export default Models
